import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Theme, media } from '../../theme';

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(247, 147, 26, 0.3); }
  50% { box-shadow: 0 0 20px rgba(247, 147, 26, 0.5); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const Banner = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(135deg, ${p => p.$theme.surface} 0%, rgba(247, 147, 26, 0.08) 100%);
  border: 2px solid ${p => p.$theme.primary};
  border-radius: 8px;
  margin: 12px 16px;
  padding: 10px 16px;
  animation: ${pulse} 3s ease-in-out infinite;
  flex-wrap: wrap;

  ${media.mobile} {
    margin: 8px;
    padding: 8px 12px;
    gap: 8px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const Title = styled.span<{ $theme: Theme }>`
  color: ${p => p.$theme.primary};
  font-weight: 700;
  font-size: 14px;
  white-space: nowrap;
  
  ${media.mobile} {
    font-size: 12px;
  }
`;

const Countdown = styled.span<{ $theme: Theme }>`
  color: ${p => p.$theme.text};
  font-size: 13px;
  opacity: 0.9;
  
  ${media.mobile} {
    font-size: 11px;
  }
`;

const Prize = styled.span<{ $theme: Theme }>`
  background: linear-gradient(90deg, ${p => p.$theme.primary}, #ffb84d, ${p => p.$theme.primary});
  background-size: 200% auto;
  animation: ${shimmer} 3s linear infinite;
  color: #000;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
  white-space: nowrap;
`;

const CenterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: center;
  min-width: 200px;
`;

const PriceInputWrapper = styled.div<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  background: ${p => p.$theme.background};
  border: 1px solid ${p => p.$theme.border};
  border-radius: 6px;
  overflow: hidden;
  
  &:focus-within {
    border-color: ${p => p.$theme.primary};
  }
`;

const PricePrefix = styled.span<{ $theme: Theme }>`
  padding: 6px 8px;
  color: ${p => p.$theme.text};
  font-weight: 600;
  font-size: 14px;
  background: ${p => p.$theme.surface};
`;

const PriceInput = styled.input<{ $theme: Theme }>`
  border: none;
  background: transparent;
  padding: 6px 10px;
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.$theme.primary};
  width: 100px;
  outline: none;
  
  &::placeholder {
    color: ${p => p.$theme.text};
    opacity: 0.4;
  }
`;

const SubmitBtn = styled.button<{ $theme: Theme; $disabled?: boolean }>`
  background: ${p => p.$disabled ? p.$theme.border : p.$theme.primary};
  color: ${p => p.$disabled ? p.$theme.text : '#000'};
  border: none;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ShareBtn = styled.a<{ $theme: Theme; $variant?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$variant === 'x' ? `
    background: #000;
    color: #fff;
    border: 1px solid rgba(255,255,255,0.2);
  ` : p.$variant === 'retweet' ? `
    background: transparent;
    color: #17bf63;
    border: 1px solid #17bf63;
  ` : `
    background: transparent;
    color: ${p.$theme.primary};
    border: 1px solid ${p.$theme.primary};
  `}
  
  &:hover {
    transform: scale(1.05);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoginBtn = styled.button<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #000;
  color: #fff;
  border: 1px solid rgba(255,255,255,0.2);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  
  &:hover {
    border-color: ${p => p.$theme.primary};
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const SuccessText = styled.span<{ $theme: Theme }>`
  color: #4caf50;
  font-weight: 600;
  font-size: 14px;
  
  span {
    color: ${p => p.$theme.primary};
  }
`;

const UserBadge = styled.span<{ $theme: Theme }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${p => p.$theme.primary};
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  background: rgba(247, 147, 26, 0.15);
  border-radius: 12px;
  white-space: nowrap;
`;

interface Props {
  theme: Theme;
  isLoggedIn: boolean;
  userHandle?: string;
  onLogin: () => void;
}

interface Contest {
  id: string;
  name: string;
  submissions_close: string;
  evaluation_time: string;
  prize_sats: number;
  entry_count: number;
  is_open: boolean;
}

interface UserEntry {
  price_prediction: number;
}

// The tweet ID of the main contest announcement (set after posting)
const CONTEST_TWEET_ID = ''; // TODO: Set this after posting announcement

export const ContestBannerSimple: React.FC<Props> = ({ theme, isLoggedIn, userHandle, onLogin }) => {
  const [contest, setContest] = useState<Contest | null>(null);
  const [userEntry, setUserEntry] = useState<UserEntry | null>(null);
  const [priceInput, setPriceInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0 });

  // Fetch contest
  useEffect(() => {
    fetch('/api/contest/active')
      .then(r => r.json())
      .then(d => d.success && d.contest && setContest(d.contest))
      .catch(() => {});
  }, []);

  // Fetch user entry
  useEffect(() => {
    if (isLoggedIn && contest) {
      const token = localStorage.getItem('authToken');
      fetch(`/api/contest/${contest.id}/my-entry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(d => d.success && d.entry && setUserEntry(d.entry))
        .catch(() => {});
    }
  }, [isLoggedIn, contest]);

  // Countdown
  useEffect(() => {
    if (!contest) return;
    const update = () => {
      const diff = new Date(contest.submissions_close).getTime() - Date.now();
      if (diff <= 0) return setCountdown({ days: 0, hours: 0, minutes: 0 });
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      });
    };
    update();
    const i = setInterval(update, 60000);
    return () => clearInterval(i);
  }, [contest]);

  const handleSubmit = async () => {
    if (!contest || !priceInput) return;
    const price = parseInt(priceInput.replace(/,/g, ''));
    if (isNaN(price) || price < 1000) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('authToken');
      const r = await fetch(`/api/contest/${contest.id}/enter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ price_prediction: price })
      });
      const d = await r.json();
      if (d.success) {
        setUserEntry({ price_prediction: price });
        setContest(prev => prev ? { ...prev, entry_count: d.entry_count } : null);
      }
    } catch {} 
    finally { setIsSubmitting(false); }
  };

  const formatNumber = (n: string) => {
    const clean = n.replace(/[^0-9]/g, '');
    return clean ? parseInt(clean).toLocaleString() : '';
  };

  // Share URLs
  const tweetText = encodeURIComponent(`🎯 My #Bitcoin price prediction for April 28, 2026:\n\n$${userEntry?.price_prediction?.toLocaleString() || '___'}\n\nMake yours at proofofprice.com\n\n@prolocodex`);
  const tweetUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;
  const retweetUrl = CONTEST_TWEET_ID ? `https://twitter.com/intent/retweet?tweet_id=${CONTEST_TWEET_ID}` : '';

  if (!contest?.is_open) return null;

  return (
    <Banner $theme={theme}>
      <LeftSection>
        <Title $theme={theme}>⚡ BTC Price Contest</Title>
        <Countdown $theme={theme}>
          {countdown.days}d {countdown.hours}h left
        </Countdown>
        <Prize $theme={theme}>🏆 {(contest.prize_sats / 1000).toFixed(0)}k sats</Prize>
      </LeftSection>

      <CenterSection>
        {userEntry ? (
          <SuccessText $theme={theme}>
            ✅ Your prediction: <span>${userEntry.price_prediction.toLocaleString()}</span>
          </SuccessText>
        ) : !isLoggedIn ? (
          <LoginBtn $theme={theme} onClick={onLogin}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
            Sign in to predict
          </LoginBtn>
        ) : (
          <>
            <UserBadge $theme={theme}>✓ @{userHandle}</UserBadge>
            <PriceInputWrapper $theme={theme}>
              <PricePrefix $theme={theme}>$</PricePrefix>
              <PriceInput
                $theme={theme}
                type="text"
                placeholder="85,000"
                value={priceInput}
                onChange={(e) => setPriceInput(formatNumber(e.target.value))}
                maxLength={10}
              />
            </PriceInputWrapper>
            <SubmitBtn 
              $theme={theme} 
              $disabled={!priceInput || isSubmitting}
              onClick={handleSubmit}
              disabled={!priceInput || isSubmitting}
            >
              {isSubmitting ? '...' : '🎯 Lock In'}
            </SubmitBtn>
          </>
        )}
      </CenterSection>

      <RightSection>
        {userEntry && (
          <>
            <ShareBtn $theme={theme} $variant="x" href={tweetUrl} target="_blank" title="Post to X">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </ShareBtn>
            {retweetUrl && (
              <ShareBtn $theme={theme} $variant="retweet" href={retweetUrl} target="_blank" title="Retweet contest">
                🔁
              </ShareBtn>
            )}
          </>
        )}
        <Countdown $theme={theme} style={{ opacity: 0.6 }}>
          {contest.entry_count} entries
        </Countdown>
      </RightSection>
    </Banner>
  );
};

export default ContestBannerSimple;
